import {
  ErpTeacherCandidateGroupsQuery,
  ErpTeacherLoadQuery,
  useErpTeacherCandidateGroupsQuery,
  useErpTeacherLoadQuery,
} from 'core/graphql';

export type ITeacherLoad = NonNullable<ErpTeacherLoadQuery['teacherLoad']>;
export type ITeacherLoadWeek = ITeacherLoad['weeks'][number];
export type ITeacherLoadApplication = ITeacherLoad['applications'][number];
export type ITeacherCandidateGroup = ErpTeacherCandidateGroupsQuery['teacherCandidateGroups'][number];

/** Lowercase strings on the wire, mirroring `load_service._band`. */
export const TEACHER_LOAD_BANDS = [
  'free',
  'light',
  'normal',
  'loaded',
] as const;
export type TeacherLoadBand = typeof TEACHER_LOAD_BANDS[number];

export const LOAD_BAND_COLOR: Record<TeacherLoadBand, string> = {
  free: 'default',
  light: 'green',
  normal: 'blue',
  loaded: 'orange',
};

interface ITeacherLoadHook {
  load?: ITeacherLoad;
  loading: boolean;
}

/**
 * Spec 0028 A — the teacher's forward-looking load.
 *
 * ⚠️ `unscheduledLessons` is a PLANNING GAP (contracted minus calendared), not
 * remaining work; `remainingLessons` is the real one. The card must never label
 * the former as «осталось провести» — with everything scheduled the gap is 0
 * while the teacher is fully booked.
 */
export const useTeacherLoad = (
  teacherId?: string,
  horizonDays?: number,
  enabled = true,
): ITeacherLoadHook => {
  const { data, loading } = useErpTeacherLoadQuery({
    // `teacherId || ''` rather than a cast: an empty required ID still satisfies
    // the contract, whereas `undefined` goes out as no variable at all and 400s
    // — which any later refetch-by-name would then trip over.
    variables: { teacherId: teacherId || '', horizonDays },
    // `enabled` is the ROLE gate. `teacherLoad` is gated on TEACHER_CARD_ROLES,
    // which excludes FinanceManager — and FinanceManager can open a teacher
    // card, so an unconditional query rejected its promise on every card open.
    skip: !enabled || !teacherId,
    fetchPolicy: 'cache-and-network',
  });
  return { load: data?.teacherLoad || undefined, loading };
};

interface ITeacherCandidateGroupsHook {
  groups: ITeacherCandidateGroup[];
  loading: boolean;
}

/**
 * «Может преподавать»: groups on a course in `coursesCanTeach` that still have
 * unstaffed future lessons. Empty (and free) when the teacher has no
 * qualifications recorded.
 */
export const useTeacherCandidateGroups = (
  teacherId?: string,
  enabled = true,
): ITeacherCandidateGroupsHook => {
  const { data, loading } = useErpTeacherCandidateGroupsQuery({
    variables: { teacherId: teacherId || '' },
    skip: !enabled || !teacherId,
    fetchPolicy: 'cache-and-network',
  });
  return { groups: data?.teacherCandidateGroups || [], loading };
};
